import { useState } from 'react'
import { motion } from 'framer-motion'
import { Bot, Calendar, Play } from 'lucide-react'
import { useNotification } from '../hooks/useNotification'

const Demo = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    jobTitle: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { showNotification } = useNotification()

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    // Simulate API call
    setTimeout(() => {
      showNotification('Demo booked successfully! We\'ll be in touch within 24 hours.', 'success')
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        jobTitle: ''
      })
      setIsSubmitting(false)
    }, 2000)
  }

  return (
    <main className="pt-20">
      <section className="section bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="container">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              See Budget-Hyre AI in Action
            </h1>
            
            <p className="text-xl text-gray-600 mb-12">
              Experience our AI interviewer firsthand. Try the interactive demo below or book a personalized session.
            </p>
          </motion.div>

          {/* Left-Right Layout */}
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left Side - Demo Options */}
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.div
                className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="text-4xl mb-4">🤖</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Try Interactive Demo
                </h3>
                <p className="text-gray-600 mb-6">
                  Experience a 5-minute AI interview simulation
                </p>
                <div className="bg-gradient-to-r from-primary-50 to-accent-50 p-4 rounded-xl border border-primary-100">
                  <p className="text-sm text-primary-700 font-medium">
                    🚧 Interactive demo coming soon!
                  </p>
                </div>
              </motion.div>

              <motion.div
                className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="text-4xl mb-4">📞</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Book Live Demo
                </h3>
                <p className="text-gray-600 mb-6">
                  Get a personalized walkthrough with our team
                </p>
                <button 
                  onClick={() => document.getElementById('booking-form').scrollIntoView({ behavior: 'smooth' })}
                  className="btn btn-primary w-full"
                >
                  Schedule Now
                </button>
              </motion.div>
            </motion.div>

            {/* Right Side - Booking Form */}
            <motion.div
              id="booking-form"
              className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm sticky top-24"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="text-center mb-8">
                <Calendar className="w-8 h-8 text-primary-500 mx-auto mb-4" />
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                  Book Your Demo
                </h2>
                <p className="text-gray-600">
                  Get a personalized walkthrough of SwiftScreen AI
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Work Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Title *
                  </label>
                  <input
                    type="text"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn btn-primary"
                >
                  {isSubmitting ? 'Booking Demo...' : 'Book Your Demo'}
                </button>

                <p className="text-xs text-gray-500 text-center mt-4">
                  🔒 We respect your privacy. Your information will only be used to schedule and conduct your demo.
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  )
}

export default Demo
